import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { sanitizeConfigOptions } from "@labxchange/util-sanitization";
import { Figure } from "@labxchange/ui-components/lib/Figure";
import plusIcon from "@common/images/plus.svg";
import LockUrl from "@common/images/locked.svg";
import { LxButton } from "@labxchange/ui-components/lib/LxButton";
import { Modal } from "@labxchange/ui-components/lib/Modal";

import { useIntl } from "react-intl";

import className from "../style.module.scss";

export function IntroModal(props) {
  const [imageUrl, setImageUrl] = useState(null);
  const intl = useIntl();

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  useEffect(() => {
    if (props.image) {
      loadImage(props.image, intl.locale)
        .then(({ imageUrl }) => {
          setImageUrl(imageUrl);
        })
        .catch((err) => {
          setImageUrl(null);
          console.error(err.message);
        });
    }
  }, [props.image, intl.locale]);

  const renderLegendItem = (icon, text) => {
    return (
      <div className={className.LegendItem}>
        {icon}
        <div>
          <FormattedMessage
            string={text}
            allowAdditionalTags={["b"]}
            description=""
          />
        </div>
      </div>
    );
  };

  const renderHotSpot = (type) => {
    let hotspotIcon = <img alt="" src={plusIcon} />;
    if (type === "locked") {
      hotspotIcon = (
        <img alt="" src={LockUrl} className={className.introModalLockedIcon} />
      );
    }

    return (
      <div
        className={`${className.introModalHotspot} ${type === "locked" ? className.introModalLocked : ""}`}
      >
        {hotspotIcon}
      </div>
    );
  };

  const loadImage = (src, lng) => {
    return new Promise((resolve, reject) => {
      const tryLoadImage = (language) => {
        const image = new Image();
        const imageUrl = src.includes(window.location.origin)
          ? src
          : `${baseUrl}locales/${language}/images/${src}`;

        image.src = imageUrl;
        image.onload = () => resolve({ imageUrl });
        image.onerror = () => {
          if (language !== "en") {
            tryLoadImage("en");
          } else {
            reject(new Error(`Failed to load image from ${imageUrl}`));
          }
        };
      };

      tryLoadImage(lng);
    });
  };

  const buttonText = {
    id: "Start",
    defaultMessage: props.buttonText ? props.buttonText : "Start",
    description: props.buttonText ? props.buttonText : "Start",
  };

  return (
    <Modal onClose={props.closeIntroModal}>
      {props.image && imageUrl && (
        <Figure src={imageUrl} altText={props.altText || ""} />
      )}
      <div id={`IntroModalIntro_`} className={className.IntroModalIntro}>
        <FormattedMessage
          string={props.text}
          sanitizeConfig={sanitizeConfigOptions.LightBlockText}
          description=""
        />
      </div>
      {!(
        props.hideHotspotInstruction && props.hideLockedHotspotInstruction
      ) && (
        <div className={className.Legend}>
          {!props.hideHotspotInstruction &&
            renderLegendItem(
              renderHotSpot("default"),
              "<b>Hotspot:</b> Click on hotspots to learn more."
            )}
          {!props.hideLockedHotspotInstruction &&
            renderLegendItem(
              renderHotSpot("locked"),
              "<b>Locked hotspot:</b> Complete the active hotspots to open the locked ones."
            )}
        </div>
      )}
      {/* Button container - centers the action button */}
      <div className={className.IntroModalButtonWrapper}>
        <LxButton
          variant="primary"
          size="large"
          tabIndex={0}
          onClickHandler={() => {
            props.closeIntroModal();
          }}
          text={buttonText.id}
          trailingIcon={!props.isLastChapter ? "next" : null}
          testId="intro-modal-start-button"
        />
      </div>
    </Modal>
  );
}

IntroModal.propTypes = {
  closeIntroModal: PropTypes.func.isRequired,
};
